import type { Catalog, CatalogEntry, InventoryEntry, MaterialCategory } from '../model/schema.js';
import { normalizeToken } from '../catalog/catalog.js';
import { matchMaterialName, materialWords, normalizeMaterialName } from './reconcile.js';
import type { MaterialRow } from './types.js';

/** Coverage of one material row by the own inventory (decision U15). */
export type StockStatus = 'in_stock' | 'partial' | 'rent';

export interface StockLength {
  length: number | null;
  needed: number;
  inStock: number;
}

export interface StockLine {
  rowKey: string;
  needed: number;
  inStock: number;
  toRent: number;
  status: StockStatus;
  /** Inventory entries (ids) the row draws from. */
  entryIds: string[];
  /** Cable rows: coverage per needed length (same order as the row's lengths). */
  lengths?: StockLength[];
}

const CABLE_CATEGORIES: ReadonlySet<MaterialCategory | undefined> = new Set<MaterialCategory>([
  'cables_audio',
  'cables_power',
  'cables_network',
]);

const CABLE_WORDS: ReadonlySet<string> = new Set(['kabel', 'cable', 'cables', 'leitung']);

/** "10 m", "10m", "2,5 m", "1.5m" inside a name. */
const LENGTH_RE = /(\d+(?:[.,]\d+)?)\s*m\b/iu;

/** Length written in a cable name ("XLR 10 m" → 10), undefined if none. */
export function lengthInName(name: string): number | undefined {
  const m = LENGTH_RE.exec(name);
  return m ? Number(m[1]!.replace(',', '.')) : undefined;
}

/** Identity of a cable type: name without lengths and the words "cable"/"Kabel". */
export function cableKey(name: string): string {
  const words = materialWords(name.replace(new RegExp(LENGTH_RE.source, 'giu'), ' '));
  return normalizeToken(words.filter((w) => !CABLE_WORDS.has(w)).join(''));
}

/** Match key of an inventory entry: cables by type, else catalog ref, else normalised name. */
function entryKey(entries: readonly CatalogEntry[], e: InventoryEntry): string {
  if (CABLE_CATEGORIES.has(e.category)) return `cable:${cableKey(e.name)}`;
  const ref = e.catalogRef ?? matchMaterialName(entries, e.name)?.ref;
  if (ref !== undefined) return `ref:${ref}`;
  return `name:${normalizeMaterialName(e.name)}`;
}

function rowKeyOf(entries: readonly CatalogEntry[], r: MaterialRow): string {
  // a renamed row (U71) is still matched by what it is
  const name = r.originalName ?? r.name;
  if (CABLE_CATEGORIES.has(r.category)) return `cable:${cableKey(name)}`;
  const ref = r.catalogRef ?? matchMaterialName(entries, name)?.ref;
  if (ref !== undefined) return `ref:${ref}`;
  return `name:${normalizeMaterialName(name)}`;
}

interface Stock {
  id: string;
  /** Cable length (m); undefined for non-cables or cables of unknown length. */
  length: number | undefined;
  left: number;
}

/** Sort length of a stock entry (no length = shortest). */
const lengthOf = (s: Stock): number => s.length ?? 0;

const statusOf = (needed: number, inStock: number): StockStatus =>
  inStock >= needed ? 'in_stock' : inStock > 0 ? 'partial' : 'rent';

/** Takes up to `want` pieces from `stock` (in order), returns the count taken. */
function take(stock: readonly Stock[], want: number, used: Set<string>): number {
  let got = 0;
  for (const s of stock) {
    const n = Math.min(s.left, want - got);
    if (n === 0) continue;
    s.left -= n;
    got += n;
    used.add(s.id);
  }
  return got;
}

/** Longest stock first (no length last): for needs of unknown length and spares. */
const longestFirst = (pool: readonly Stock[]): Stock[] =>
  [...pool].sort((a, b) => lengthOf(b) - lengthOf(a));

/** Shortest stock that is at least `length` long first; stock without a length never fits. */
const fittingFirst = (pool: readonly Stock[], length: number): Stock[] =>
  pool.filter((s) => lengthOf(s) >= length).sort((a, b) => lengthOf(a) - lengthOf(b));

/**
 * Allocates the inventory to the material list (decision U15): entries form a shared pool used
 * row by row in list order; matching by catalog ref, else normalised name; cables by type and
 * length (shortest fitting stock; unknown lengths and spares take the longest left). Returns
 * one line per row (rows without any match: everything to rent).
 */
export function allocateStock(
  rows: readonly MaterialRow[],
  inventory: readonly InventoryEntry[],
  catalogs: readonly Catalog[],
): Map<string, StockLine> {
  const entries = catalogs.flatMap((c) => c.entries);
  const pools = new Map<string, Stock[]>();
  for (const e of inventory) {
    const key = entryKey(entries, e);
    const stock: Stock = { id: e.id, length: e.length ?? lengthInName(e.name), left: e.quantity };
    pools.set(key, [...(pools.get(key) ?? []), stock]);
  }
  const out = new Map<string, StockLine>();
  // merged positions (U71) draw for each of their rows
  for (const r of rows.flatMap((row) => row.members ?? [row])) {
    const pool = pools.get(rowKeyOf(entries, r)) ?? [];
    const used = new Set<string>();
    let lengths: StockLength[] | undefined;
    let inStock: number;
    if (r.lengths !== undefined && CABLE_CATEGORIES.has(r.category)) {
      lengths = r.lengths.map((l) => ({
        length: l.length,
        needed: l.count,
        inStock: take(
          l.length === null ? longestFirst(pool) : fittingFirst(pool, l.length),
          l.count,
          used,
        ),
      }));
      const counted = lengths.reduce((n, l) => n + l.needed, 0);
      inStock =
        lengths.reduce((n, l) => n + l.inStock, 0) +
        take(longestFirst(pool), r.total - counted, used);
    } else {
      inStock = take(pool, r.total, used);
    }
    out.set(r.rowKey, {
      rowKey: r.rowKey,
      needed: r.total,
      inStock,
      toRent: r.total - inStock,
      status: statusOf(r.total, inStock),
      entryIds: [...used],
      ...(lengths !== undefined ? { lengths } : {}),
    });
  }
  for (const m of rows) {
    if (m.members === undefined) continue;
    const lines = m.members.map((r) => out.get(r.rowKey)!);
    const inStock = Math.min(
      m.total,
      lines.reduce((n, l) => n + l.inStock, 0),
    );
    out.set(m.rowKey, {
      rowKey: m.rowKey,
      needed: m.total,
      inStock,
      toRent: m.total - inStock,
      status: statusOf(m.total, inStock),
      entryIds: [...new Set(lines.flatMap((l) => l.entryIds))],
    });
  }
  return out;
}

/** Number of rows with something to rent and the pieces to rent in total. */
export function rentalSummary(lines: Iterable<StockLine>): { rows: number; pieces: number } {
  let rows = 0;
  let pieces = 0;
  for (const l of lines) {
    if (l.toRent <= 0) continue;
    rows += 1;
    pieces += l.toRent;
  }
  return { rows, pieces };
}
